import { useEffect, useRef, useState } from 'react';
import { flushSync } from 'react-dom';

import { CookieNotice } from './components/CookieNotice';
import { SiteFooter } from './components/SiteFooter';
import { SiteHeader } from './components/SiteHeader';
import { startMotion } from './motion';
import { findRoute, NOT_FOUND } from './routes';

type Props = {
  // Set by scripts/prerender.js; in the browser the current URL decides.
  path?: string;
};

// Every page is prerendered to its own HTML file, so a first visit or a
// reload is a normal page load. After that, links between the site's own
// pages swap the page in place (crossfaded where the browser supports view
// transitions) instead of loading a new document, so nothing ever blanks or
// flashes between pages.
function App({ path }: Props) {
  const [pathname, setPathname] = useState(() => path ?? window.location.pathname);
  const shown = useRef(pathname);
  shown.current = pathname;
  const { title, Page } = findRoute(pathname);

  useEffect(() => {
    document.title = title;
  }, [title]);

  // Each page's blocks reveal themselves once; a new page gets its own run.
  useEffect(() => startMotion(), [pathname]);

  useEffect(() => {
    const go = (url: URL, push: boolean) => {
      const swap = () => {
        flushSync(() => setPathname(url.pathname));
        if (push) window.history.pushState(null, '', url.pathname + url.search + url.hash);
        // Jump, don't glide: the crossfade is the transition.
        const instant = 'instant' as ScrollBehavior;
        const target = url.hash && document.getElementById(decodeURIComponent(url.hash.slice(1)));
        if (target) target.scrollIntoView({ behavior: instant });
        else window.scrollTo({ top: 0, behavior: instant });
      };
      const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (document.startViewTransition && !reduce) document.startViewTransition(swap);
      else swap();
    };

    const onClick = (event: MouseEvent) => {
      if (event.defaultPrevented || event.button !== 0) return;
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      const link = (event.target as Element | null)?.closest?.('a');
      if (!link || (link.target && link.target !== '_self') || link.hasAttribute('download')) return;
      const url = new URL(link.href, window.location.href);
      if (url.origin !== window.location.origin) return;
      // Same page (e.g. "/#faq" while on "/"): let the browser scroll.
      if (url.pathname === window.location.pathname) return;
      if (findRoute(url.pathname) === NOT_FOUND) return;
      event.preventDefault();
      go(url, true);
    };
    // Back/forward between pages. A hash-only step on the same page is the
    // browser's own scroll and is left to it.
    const onPopState = () => {
      const url = new URL(window.location.href);
      if (url.pathname !== shown.current) go(url, false);
    };

    document.addEventListener('click', onClick);
    window.addEventListener('popstate', onPopState);
    return () => {
      document.removeEventListener('click', onClick);
      window.removeEventListener('popstate', onPopState);
    };
  }, []);

  return (
    <>
      <a className="skip-link" href="#main">
        Skip to content
      </a>
      <SiteHeader />
      <main id="main">
        <Page />
      </main>
      <SiteFooter />
      <CookieNotice path={pathname} />
    </>
  );
}

export default App;
